import { Box, Stack, Typography, useTheme } from "@mui/material";
import { useFormContext } from "react-hook-form";
import { useEffect, useState } from "react";

export const SignaturePreviewSection = ({
  options,
  previewValues,
}: {
  options: Record<string, any>;
  previewValues: Record<string, any>;
}) => {
  const theme = useTheme();

  const { register, control, watch, setValue } = useFormContext();
  const [signatureHtml, setSignatureHtml] = useState("");
  const {
    signature_logo: logo,
    position: position,
    width: width,
    height: height,
    signature_text: text,
  } = watch();

  useEffect(() => {
    // console.log(options, previewValues);
    const outerBox = document.querySelector(".dd-signature-preview-box");
    if (!outerBox) return;

    // Clear all existing children
    while (outerBox.firstChild) {
      outerBox.removeChild(outerBox.firstChild);
    }

    // Create a table instead of a div with flex
    const previewTable = document.createElement("table");
    previewTable.className = "dd-signature-box";
    previewTable.style.width = "fit-content";
    previewTable.style.maxWidth = "100%";
    previewTable.style.minHeight = "100px";
    previewTable.style.borderCollapse = "separate";
    previewTable.style.borderSpacing = "16px 0";
    outerBox.appendChild(previewTable);

    // Create a tbody
    const tbody = document.createElement("tbody");
    previewTable.appendChild(tbody);

    // Create a table row
    let tr;

    // Initialize elements
    const imgElement = logo ? document.createElement("img") : null;
    const textDiv = text ? document.createElement("div") : null;

    // Configure image if it exists
    if (imgElement) {
      imgElement.src = logo;
      imgElement.style.width = `${width || 100}px`;
      imgElement.style.height = `${height || 100}px`;
      imgElement.style.objectFit = "fill";
      imgElement.style.verticalAlign = "top";
    }

    // Configure text div if it exists
    if (textDiv) {
      textDiv.className = "dd-signature-text";

      // Parse the text to replace .dd-signature-chip elements
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, "text/html");
      textDiv.innerHTML = doc.body.innerHTML;

      if (textDiv instanceof HTMLElement) {
        textDiv.style.maxWidth = "100%";
        textDiv.style.overflow = "hidden";
        textDiv.style.alignItems = "flex-start";
        textDiv.style.overflowWrap = "break-word";
        textDiv.style.verticalAlign = "top";

        // textDiv.style.fontFamily =
        //   theme.typography["body-large"].fontFamily ?? "";
        // textDiv.style.fontSize = `${
        //   theme.typography["body-large"].fontSize ?? "16px"
        // }`;
        // textDiv.style.fontWeight = `${
        //   theme.typography["body-large"].fontWeight ?? "400"
        // }`;
        // textDiv.style.lineHeight = `${
        //   theme.typography["body-large"].lineHeight ?? "24px"
        // }`;
        // textDiv.style.letterSpacing = `${
        //   theme.typography["body-large"].letterSpacing ?? "0px"
        // }`;
        // textDiv.style.color = `${theme.palette.lightBg.low ?? "black"}`;
      }
    }

    // Arrange elements based on position
    switch (position) {
      case "left":
        tr = document.createElement("tr");
        tbody.appendChild(tr);

        if (imgElement) {
          const logoCell = document.createElement("td");
          logoCell.appendChild(imgElement);
          tr.appendChild(logoCell);
        }

        if (textDiv) {
          const textCell = document.createElement("td");
          textCell.appendChild(textDiv);
          tr.appendChild(textCell);
        }
        break;

      case "right":
        tr = document.createElement("tr");
        tbody.appendChild(tr);

        if (textDiv) {
          const textCell = document.createElement("td");
          textCell.appendChild(textDiv);
          tr.appendChild(textCell);
        }

        if (imgElement) {
          const logoCell = document.createElement("td");
          logoCell.appendChild(imgElement);
          tr.appendChild(logoCell);
        }
        break;

      case "top":
        if (imgElement) {
          tr = document.createElement("tr");
          tbody.appendChild(tr);
          const logoCell = document.createElement("td");
          logoCell.appendChild(imgElement);
          tr.appendChild(logoCell);
        }

        if (textDiv) {
          tr = document.createElement("tr");
          tbody.appendChild(tr);
          const textCell = document.createElement("td");
          textCell.appendChild(textDiv);
          tr.appendChild(textCell);
        }
        break;

      case "bottom":
        if (textDiv) {
          tr = document.createElement("tr");
          tbody.appendChild(tr);
          const textCell = document.createElement("td");
          textCell.appendChild(textDiv);
          tr.appendChild(textCell);
        }

        if (imgElement) {
          tr = document.createElement("tr");
          tbody.appendChild(tr);
          const logoCell = document.createElement("td");
          logoCell.appendChild(imgElement);
          tr.appendChild(logoCell);
        }
        break;
    }

    // Set the innerHTML of previewBox to signature_html
    setValue("signature_html", outerBox.innerHTML);

    // Replace .dd-template-chip elements in signature_html
    const chips = previewTable.querySelectorAll(".dd-template-chip");
    chips.forEach((chip) => {
      const chipData = chip.getAttribute("data-value") || "";
      const span: any = document.createElement("span");
      span.setAttribute("data-value", chipData);
      span.style.marginRight = "4px";
      const option = options.find(
        (option: any) => option.value === chipData,
      )?.data_key;

      span.textContent = previewValues?.[option] || "";
      chip.replaceWith(span);
    });

    // Set the signature HTML for rendering
    const previewContent = previewTable.outerHTML;
    setSignatureHtml(previewContent);
  }, [logo, position, width, height, text, previewValues, options]);

  const getNestedValue = (obj: Record<string, any>, path: string) => {
    return path.split(".").reduce((acc, part) => acc && acc[part], obj);
  };

  return (
    <Stack
      flex={1}
      direction="column-reverse"
      spacing={2}
      sx={{
        borderRadius: "8px",
        background:
          "linear-gradient(270deg, rgba(240, 214, 255, 0.2) 0%, rgba(211, 205, 255, 0.2) 100%)",
        height: "100%",
        overflowY: "hidden",
        p: "0px 34px 54px 34px",
      }}
    >
      <Stack
        justifyContent={"space-between"}
        alignItems={"center"}
        sx={{
          p: "40px",
          width: "100%",
          minHeight: "100%",
          bgcolor: theme.palette.darkBg.main,
          borderRadius: "0px 0px 12px 12px",
          alignItems: "start",
          overflowY: "hidden",
          position: "relative",
        }}
        gap={5}
      >
        <Stack
          direction={"row"}
          spacing={2}
          width={"100%"}
          justifyContent={"end"}
          position={"absolute"}
          top={"24px"}
          right={"24px"}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: "0px 8px",
              height: "24px",
              borderRadius: "4px",
              border: "1px solid #6600FA",
              bgcolor: "#F1E8FF",
              // transform: "translate(16px, -16px)",
            }}
          >
            <Typography
              variant="caption-2"
              color="#6600FA"
              textTransform={"uppercase"}
            >
              Preview
            </Typography>
          </Box>
        </Stack>
        <Box height={"50px"}></Box>
        {/* email placeholder */}
        <Stack
          width={"100%"}
          flex={5}
          overflow={"visible"}
          gap={2}
          justifyContent={"space-between"}
        >
          <Stack gap={1}>
            {["10%", "100%", "100%", "50%"].map((width, index) => (
              <Box
                key={`top_${width}-${index}`}
                width={width}
                height={"10px"}
                sx={{
                  borderRadius: "12px",
                  bgcolor: theme.palette.neutral["015"],
                }}
              ></Box>
            ))}
          </Stack>
        </Stack>
        {/* email placeholder */}
        {/* signature preview */}
        <Box
          className="dd-signature-preview-box"
          dangerouslySetInnerHTML={{ __html: signatureHtml }}
          sx={{
            maxHeight: "300px", // Set your desired maximum height
            overflowY: "auto", // Enable vertical scrolling when content exceeds maxHeight
          }}
        />
        {/* signature preview */}
      </Stack>
    </Stack>
  );
};
